import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import classnames from 'classnames';

import slugify from 'common/util/slugify';

import {
  bedsDisplayString,
  bathsDisplayString,
  formatPrice,
} from 'common/formatters';

import {
  LISTING_PHOTO_DIMENSION_CARD,
  LISTING_IMAGE_PLACEHOLDER_URL,
  LISTING_SERVICE_TYPE_TENANT_SCREENING,
} from 'common/listingConstants';

import {
  isListingTenantScreening,
  getListingFieldsForDisplay,
  getListingUrlPath,
  getListingManagedPhotoUrls,
} from 'common/listingHelpers';

import {
  calculateNetEffectiveRent,
} from 'common/concessionHelpers';

import branch from 'client/core/branch';

import SrLink from 'client/components/SrLink/SrLink';
import SrBox from 'client/components/SrBox';
import SrExternalLink from 'client/components/SrExternalLink';
import SrConcessionsBanner from 'client/components/SrConcessionsBanner/SrConcessionsBanner';

@withRouter
@branch({
  appUrl: ['env', 'APP_URL'],
})
class SrListingThumbnail extends Component {
  static propTypes = {
    useSrExternalLink: PropTypes.bool,
    listing: PropTypes.object.isRequired,
    concession: PropTypes.object,
    currentUser: PropTypes.object,
    style: PropTypes.object,
    agentTourId: PropTypes.number,
    adminTourId: PropTypes.number,
    photoResolution: PropTypes.number.isRequired,
    bookmarksByListingId: PropTypes.object,
    requestedFrom: PropTypes.string,
    managementCompanyIframe: PropTypes.string,
    className: PropTypes.string,
    appUrl: PropTypes.string.isRequired,
    onClick: PropTypes.func,
  }

  render() {
    const {
      useSrExternalLink,
      listing,
      concession,
      currentUser,
      style,
      agentTourId,
      adminTourId,
      photoResolution,
      requestedFrom,
      className,
      managementCompanyIframe,
      appUrl,
      onClick,
    } = this.props;

    const listingTenantScreening = isListingTenantScreening(listing);

    const managedPhotoUrls = getListingManagedPhotoUrls({
      listing: listing,
      dimension: LISTING_PHOTO_DIMENSION_CARD,
      resolution: photoResolution,
    });

    const photoUrls = [
      ...managedPhotoUrls,
      ...(listing.apartmentCommunityPhotoUrls || []),
    ];

    if (!photoUrls.length) {
      photoUrls.push(LISTING_IMAGE_PLACEHOLDER_URL);
    }

    const listingFieldsForDisplay = getListingFieldsForDisplay({
      listing: listing,
      searchFilters: currentUser?.searchFilters,
    });

    let listingUrl = getListingUrlPath(listing, { agentTourId, requestedFrom, adminTourId });
    let target = null;

    if (managementCompanyIframe) {
      listingUrl = (
        `${appUrl}${listingUrl}?utm_source=property-manager&utm_medium=iframe-widget` +
        `&utm_campaign=${encodeURIComponent(slugify(managementCompanyIframe))}&utm_content=listing-card`
      );
      target = '_blank';
    }

    let Link = useSrExternalLink ? SrExternalLink : SrLink;
    let linkProps = useSrExternalLink
      ? { className: listing.dataSource, link: listingUrl }
      : { className: listing.dataSource, to: listingUrl, target: target };

    if (listingTenantScreening) {
      Link = SrBox;
      linkProps = {};
    }

    return (
      <div
        className={classnames(
          'searchLinkContainer',
          className,
          listingTenantScreening && LISTING_SERVICE_TYPE_TENANT_SCREENING,
        )}
        style={style}
        onClick={onClick}
      >
        <Link {...linkProps}>
          <div className='thumbnailContainer'>
            <div
              className='thumbnailImage'
              style={{ backgroundImage: `url(${photoUrls[0]})` }}
              alt='Listing'
            />
            <div>
              <div className='thumbnailAddress'>
                <span>{listing.addressStreetDisplay}</span>
                {listing.apartmentUnit ? <span>&nbsp;&nbsp;#{listing.apartmentUnit}</span> : null}
              </div>
              <div className='statsContainer'>
                <span className='statsText'>
                  {formatPrice(concession
                    ? (
                      calculateNetEffectiveRent({
                        concession: concession,
                        rentPrice: listing.price,
                      }).netEffectiveRent
                    )
                    : listingFieldsForDisplay.price
                  )}
                </span>
                {listing.bedrooms != null && (
                  <>
                    <span className='pipe'> | </span>
                    <span className='statsNumber'>
                      {bedsDisplayString(listingFieldsForDisplay).split(' ')[0]}
                    </span>
                    <span>{bedsDisplayString(listingFieldsForDisplay).split(' ')[1]}</span>
                  </>
                )}
                {listing.bathrooms != null && (
                  <>
                    <span className='pipe'> | </span>
                    <span className='statsNumber'>
                      {bathsDisplayString(listingFieldsForDisplay).split(' ')[0]}
                    </span>
                    <span>{bathsDisplayString(listingFieldsForDisplay).split(' ')[1]}</span>
                  </>
                )}
              </div>
              {listing.propertyType && (
                <div className='propertyTypePill'>{listing.propertyType}</div>
              )}
            </div>
          </div>
        </Link>
        {concession && (
          <SrBox
            margin='0px 18px 18px 18px'
          >
            <SrConcessionsBanner
              concession={concession}
              rentPrice={listing.price}
            />
          </SrBox>
        )}
      </div>
    );
  }
}

SrListingThumbnail.displayName = 'SrListingThumbnail';

export default SrListingThumbnail;
